import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { Button, Card, StatusPill } from '../../components/primitives/index.js'
import { STATUS_META, Timeline, ShipmentCard, fmtDateTime } from '../TrackOrderPage.jsx'
import { useCart } from '../../context/CartContext.jsx'
import { useProducts } from '../../context/ProductsContext.jsx'
import { fmtMoney } from '../../utils/invoiceUtils.js'
import './AccountOrders.css'

/** Tab component for AccountPage's `orders` tab — orders/loading/customer
 * are fetched once at the shell level and passed down, mirroring Prod. */
export default function AccountOrders({ orders, loading, customer }) {
  const [openInvoice, setOpenInvoice] = useState(null)
  const [reorderMsg, setReorderMsg] = useState('')
  const { addItem } = useCart()
  const { getProductById } = useProducts()
  const navigate = useNavigate()

  function viewInvoice(order) {
    navigate('/invoice', { state: { order } })
  }

  function reorder(order) {
    let added = 0
    let missing = 0
    for (const item of order.items ?? []) {
      const product = getProductById(item.productId)
      const variant = product?.variants?.find((v) => String(v.id) === String(item.variantId))
      if (product && variant) {
        addItem(product, variant, item.qty ?? 1)
        added += 1
      } else {
        missing += 1
      }
    }
    if (added === 0) {
      setReorderMsg('These items are no longer available.')
      return
    }
    setReorderMsg(missing > 0 ? `${added} added to cart — ${missing} no longer available.` : '')
    if (added > 0 && missing === 0) navigate('/checkout')
  }

  if (loading) return <Card className="account-orders__empty">Loading your orders&hellip;</Card>

  if (!customer?.emailVerified) {
    return <Card className="account-orders__empty">Confirm your email to see your order history.</Card>
  }

  if (!orders?.length) {
    return (
      <Card className="account-orders__empty">
        <p>No orders yet.</p>
        <Button to="/catalog" variant="primary" size="sm">
          Browse the catalog
        </Button>
      </Card>
    )
  }

  return (
    <div className="account-orders">
      {reorderMsg && <p className="account-orders__msg">{reorderMsg}</p>}
      {orders.map((order) => {
        const meta = STATUS_META[order.status] ?? { label: order.status, color: '#a3a3a3' }
        const open = openInvoice === order.invoiceNumber
        return (
          <Card key={order.invoiceNumber} className="account-orders__row" style={{ '--status-color': meta.color }}>
            <div className="account-orders__row-top">
              <div>
                <span className="account-orders__invoice">{order.invoiceNumber}</span>
                <span className="account-orders__date">{fmtDateTime(order.createdAt)}</span>
              </div>
              <StatusPill label={meta.label} color={meta.color} />
            </div>

            <div className="account-orders__items">
              {order.items.map((item) => (
                <span key={item.lineId ?? item.name}>
                  {item.name} ({item.concentration ?? item.size}) &times;{item.qty}
                </span>
              ))}
            </div>

            <div className="account-orders__row-bottom">
              <span className="account-orders__total">{fmtMoney(order.total)}</span>
              <div className="account-orders__actions">
                <Button size="sm" variant="ghost" onClick={() => setOpenInvoice(open ? null : order.invoiceNumber)}>
                  {open ? 'Hide details' : 'View details'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => viewInvoice(order)}>
                  Print invoice
                </Button>
                <Button size="sm" variant="secondary" onClick={() => reorder(order)}>
                  Reorder
                </Button>
              </div>
            </div>

            <AnimatePresence initial={false}>
              {open && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="account-orders__detail">
                    {order.shipments?.map((s, i) => (
                      <ShipmentCard key={i} shipment={s} />
                    ))}
                    <Timeline history={order.history} />

                    <div className="account-orders__breakdown">
                      <div className="account-orders__lineitems">
                        {order.items.map((item, i) => {
                          const unitPrice = item.effectivePrice ?? item.price ?? 0
                          return (
                            <div key={item.lineId ?? i} className="account-orders__lineitem">
                              <span className="account-orders__lineitem-name">
                                {item.name} {item.concentration ?? item.size ? `(${item.concentration ?? item.size})` : ''}
                                {item.preorder && <span className="account-orders__lineitem-preorder">Pre-order</span>}
                              </span>
                              <span className="account-orders__lineitem-qty">&times;{item.qty}</span>
                              <span className="account-orders__lineitem-price">{fmtMoney(unitPrice * item.qty)}</span>
                            </div>
                          )
                        })}
                      </div>

                      <div className="account-orders__totals">
                        <div className="account-orders__totals-row">
                          <span>Subtotal</span>
                          <span>{fmtMoney(order.subtotal)}</span>
                        </div>
                        {(order.discountAmount ?? 0) > 0 && (
                          <div className="account-orders__totals-row account-orders__totals-row--credit">
                            <span>Discount{order.discountCode ? ` (${order.discountCode})` : ''}</span>
                            <span>&minus;{fmtMoney(order.discountAmount)}</span>
                          </div>
                        )}
                        <div className="account-orders__totals-row">
                          <span>Shipping</span>
                          <span>{!order.shipping ? 'Free' : fmtMoney(order.shipping)}</span>
                        </div>
                        {(order.insuranceAmount ?? 0) > 0 && (
                          <div className="account-orders__totals-row">
                            <span>{order.insuranceLabel || 'Insurance'}</span>
                            <span>{fmtMoney(order.insuranceAmount)}</span>
                          </div>
                        )}
                        {(order.redeemedAmount ?? 0) > 0 && (
                          <div className="account-orders__totals-row account-orders__totals-row--credit">
                            <span>Points redeemed{order.redeemedPoints ? ` (${order.redeemedPoints} pts)` : ''}</span>
                            <span>&minus;{fmtMoney(order.redeemedAmount)}</span>
                          </div>
                        )}
                        {(order.creditUsed ?? 0) > 0 && (
                          <div className="account-orders__totals-row account-orders__totals-row--credit">
                            <span>Store credit applied</span>
                            <span>&minus;{fmtMoney(order.creditUsed)}</span>
                          </div>
                        )}
                        <div className="account-orders__totals-row account-orders__totals-row--final">
                          <span>Total</span>
                          <span>{fmtMoney(order.total)}</span>
                        </div>
                      </div>

                      {order.billingInfo && (
                        <div className="account-orders__billing">
                          <p className="account-orders__billing-label">Shipping to</p>
                          <p>{order.billingInfo.name}</p>
                          <p>
                            {order.billingInfo.address}{order.billingInfo.apt ? `, ${order.billingInfo.apt}` : ''}<br />
                            {[order.billingInfo.city, order.billingInfo.state, order.billingInfo.zip].filter(Boolean).join(', ')}<br />
                            {order.billingInfo.country}
                          </p>
                          {order.billingInfo.email && <p>{order.billingInfo.email}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        )
      })}
    </div>
  )
}
